import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode, useState } from "react";
import { createRoot } from "react-dom/client";
import { ClinicalRuntimeProvider } from "./auth/ClinicalRuntimeProvider";
import { EmrPageRelay } from "./emr-import/EmrPageRelay";
import { EMR_PAGE_RECEIVER_PATH } from "./emr-import/pageCapture";
import { router } from "./router";
import { loadClinicalRuntimeConfig } from "./runtime/clinicalRuntime";
import "pdfjs-dist/web/pdf_viewer.css";
import "./styles.css";

function AuthenticatedWorkbench() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            gcTime: 15 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 30 * 1000,
          },
          mutations: { retry: 0 },
        },
      }),
  );
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ queryClient }} />
    </QueryClientProvider>
  );
}

function ClinicalApp() {
  return (
    <ClinicalRuntimeProvider>
      <AuthenticatedWorkbench />
    </ClinicalRuntimeProvider>
  );
}

/**
 * The "Send to BobDoc" bookmark's tab (EXECDEV-227) relays the chart before any
 * sign-in; only when the physician registers the patient here does it become
 * the signed-in app, at the same address.
 */
function EmrPageRelayRoot() {
  const [registerHere, setRegisterHere] = useState(false);
  const [enabled] = useState(() => {
    try {
      return loadClinicalRuntimeConfig().emrPageImportEnabled === true;
    } catch {
      return false;
    }
  });
  if (registerHere) return <ClinicalApp />;
  return (
    <EmrPageRelay
      enabled={enabled}
      allowLoopback={import.meta.env.DEV === true}
      opener={window.opener as Window | null}
      onRegisterHere={() => setRegisterHere(true)}
    />
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("Application root was not found");

createRoot(root).render(
  <StrictMode>
    {window.location.pathname === EMR_PAGE_RECEIVER_PATH ? <EmrPageRelayRoot /> : <ClinicalApp />}
  </StrictMode>,
);

// Live UI previews should load the latest deployment instead of the offline shell.
if (
  import.meta.env.PROD &&
  import.meta.env.VITE_DISABLE_SERVICE_WORKER !== "true" &&
  "serviceWorker" in navigator
) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js");
  });
}
